import { useCallback, useEffect, useState } from 'react'

import { getIconForDataConnectionType } from 'features/DataConnections/getIconForDataConnectionType'

import { useTabsContext } from 'ui/components/Tabs/tabsContext'
import { useDoubleClick } from 'ui/helpers/useDoubleClick'

type UseObjectTabTriggerStateProps = {
    object: ObjectDto
    dataConnection?: DataConnectionDto
    onSelectObject: (objectSid: string) => void
}

export function useObjectTabTriggerState({
    object,
    dataConnection,
    onSelectObject,
}: UseObjectTabTriggerStateProps) {
    const iconUrl = getIconForDataConnectionType(dataConnection?.type ?? 'native_tables')
    const iconLabel = dataConnection?.label

    const { value } = useTabsContext()

    const isActive = value === object._sid

    const [isConfigDropdownOpen, setIsConfigDropdownOpen] = useState(false)

    const onConfigDropdownOpenChange = (open: boolean) => {
        setIsConfigDropdownOpen(open)
    }

    const [isRenamePopupOpen, setIsRenamePopupOpen] = useState(false)
    const onRenamePopupOpenChange = (open: boolean) => {
        setIsRenamePopupOpen(open)
    }

    const executeSingleClick = useCallback(() => {
        if (!isActive) {
            return
        }

        if (isRenamePopupOpen) {
            setIsRenamePopupOpen(false)
        } else {
            // Toggle the dropdown when the tab is active.
            setIsConfigDropdownOpen((open) => !open)
        }
    }, [isActive, isRenamePopupOpen])

    const executeDoubleClick = useCallback(() => {
        if (!isActive) return

        if (isConfigDropdownOpen) {
            setIsConfigDropdownOpen(false)
        } else {
            // Open the rename popup when the tab is active.
            setIsRenamePopupOpen((open) => !open)
        }
    }, [isActive, isConfigDropdownOpen])

    const handleDoubleClick = useDoubleClick(executeSingleClick, executeDoubleClick)

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()

        // Do this immediately to avoid the janky double click behavior.
        if (!isActive) {
            onSelectObject(object._sid)
        }

        handleDoubleClick(e)
    }

    const onOpenRenamePopup = useCallback(() => {
        setIsRenamePopupOpen(true)
    }, [])

    // Close the dropdown when the tab is not active.
    useEffect(() => {
        if (!isActive) {
            setIsConfigDropdownOpen(false)
            setIsRenamePopupOpen(false)
        }
    }, [isActive])

    return {
        iconUrl,
        iconLabel,
        isActive,
        isConfigDropdownOpen,
        onConfigDropdownOpenChange,
        isRenamePopupOpen,
        onRenamePopupOpenChange,
        handleClick,
        onOpenRenamePopup,
    }
}
